import { Schema } from "effect";
import {
  ClaimChartPreviewSchema,
  FriendChartSummarySchema,
  SavedChartLibraryItemSchema,
} from "./chart-schema";
import { SaveSocialProfileRequestSchema } from "./friends";

const FriendshipIdsSchema = Schema.Struct({
  action: Schema.Literal("acknowledge-acceptances"),
  friendshipIds: Schema.Array(Schema.String),
});

export const FriendsActionSchema = Schema.Union([
  Schema.Struct({
    action: Schema.Literal("save-profile"),
    ...SaveSocialProfileRequestSchema.fields,
  }),
  Schema.Struct({ action: Schema.Literal("request"), username: Schema.String }),
  Schema.Struct({
    action: Schema.Literal("respond"),
    friendshipId: Schema.String,
    response: Schema.Literals(["accepted", "declined"] as const),
  }),
  FriendshipIdsSchema,
  Schema.Struct({ action: Schema.Literal("remove"), friendshipId: Schema.String }),
  Schema.Struct({ action: Schema.Literal("block"), friendshipId: Schema.String }),
  Schema.Struct({ action: Schema.Literal("create-invite") }),
  Schema.Struct({ action: Schema.Literal("create-claim-invite"), chartId: Schema.String }),
  Schema.Struct({
    action: Schema.Literals(["preview-invite", "accept-invite", "claim-invite"] as const),
    token: Schema.String,
  }),
]);

export type FriendsAction = typeof FriendsActionSchema.Type;

export const FriendsQuerySchema = Schema.Struct({
  friendshipId: Schema.optional(Schema.String),
  username: Schema.optional(Schema.String),
});

export type FriendsQuery = typeof FriendsQuerySchema.Type;

const FriendChartSummaryResponseSchema = FriendChartSummarySchema;

const FriendSummaryResponseSchema = Schema.Struct({
  friendshipId: Schema.String,
  connectedAt: Schema.optional(Schema.String),
  username: Schema.String,
  displayName: Schema.String,
  allowCompatibility: Schema.Boolean,
  profilePhoto: Schema.optional(Schema.String),
  chart: Schema.NullOr(FriendChartSummaryResponseSchema),
});

export const FriendsDashboardResponseSchema = Schema.Struct({
  profile: Schema.NullOr(
    Schema.Struct({
      username: Schema.String,
      displayName: Schema.String,
      primaryChartId: Schema.String,
      allowCompatibility: Schema.Boolean,
      isPublic: Schema.optional(Schema.Boolean),
      profilePhoto: Schema.optional(Schema.String),
    }),
  ),
  incoming: Schema.Array(FriendSummaryResponseSchema),
  outgoing: Schema.Array(FriendSummaryResponseSchema),
  friends: Schema.Array(FriendSummaryResponseSchema),
  blocked: Schema.Array(FriendSummaryResponseSchema),
  acceptedNotifications: Schema.Array(FriendSummaryResponseSchema),
});

export const FriendChartResponseSchema = Schema.Struct({
  friendshipId: Schema.String,
  username: Schema.String,
  displayName: Schema.String,
  allowCompatibility: Schema.Boolean,
  profilePhoto: Schema.optional(Schema.String),
  chart: SavedChartLibraryItemSchema,
});

export const InvitePreviewResponseSchema = Schema.Struct({
  inviterName: Schema.String,
  kind: Schema.Literals(["connection", "claim-chart"] as const),
  chart: Schema.optional(ClaimChartPreviewSchema),
});

export const InviteUrlResponseSchema = Schema.Struct({ inviteUrl: Schema.String });

export const PublicChartResponseSchema = Schema.Struct({
  username: Schema.optional(Schema.String),
  displayName: Schema.optional(Schema.String),
  allowCompatibility: Schema.Boolean,
  profilePhoto: Schema.optional(Schema.String),
  chart: FriendChartSummarySchema,
});

export const FriendsDashboardOrChartResponseSchema = Schema.Union([
  FriendsDashboardResponseSchema,
  FriendChartResponseSchema,
]);

export const FriendsActionResponseSchema = Schema.Union([
  FriendsDashboardResponseSchema,
  InviteUrlResponseSchema,
  InvitePreviewResponseSchema,
]);

export type FriendsGetResponse = typeof FriendsDashboardOrChartResponseSchema.Type;
export type FriendsPostResponse = typeof FriendsActionResponseSchema.Type;
